import saudi from './saudi.json';

/**
 * Zero-network L1 cache for region translations.
 *
 * Build-time bundle of every top-of-tree Saudi region (provinces, governorates,
 * top cities, all hubs) plus their en/ar/ur display names. Mobile drivers and
 * web order-list renderers consult this map first; only if the customCode is
 * missing do they fall through to L2 (TanStack Query) → L3 (the batched
 * `region.translate` endpoint).
 *
 * Curated manually in `saudi.json` (GCC countries + Saudi hierarchy +
 * major logistics hubs). Edit the JSON directly when extending L1.
 *
 * See `docs/regions/01-translation-and-cache.md` for the full 5-layer cache stack.
 */

/** Two-letter language code used by the static bundle. Matches `languageSchema`. */
export type StaticRegionLang = 'en' | 'ar' | 'ur';

/** One static-bundle entry. */
export type StaticRegionEntry = {
  /** Display names per language. `en` is mandatory; `ar` / `ur` are optional. */
  displayName: { en: string; ar?: string; ur?: string };
  /** Region kind — `country | state | governorate | city | neighborhood | hub`. */
  type: string;
};

/** Strongly-typed shape of `saudi.json`. */
export type StaticRegionBundle = {
  _meta: { generatedAt: string; source: string; note?: string };
  regions: Record<string, StaticRegionEntry>;
};

const SAUDI_BUNDLE: StaticRegionBundle = saudi as StaticRegionBundle;

/**
 * Look up a region's display name in the requested language with English fallback.
 *
 * Returns `undefined` when the `customCode` is not in the static bundle —
 * callers should fall through to TanStack Query (`useTranslateRegions`).
 */
export function getStaticRegion(
  customCode: string,
  lang: StaticRegionLang = 'en',
): { displayName: string; type: string } | undefined {
  const entry = SAUDI_BUNDLE.regions[customCode];
  if (!entry) return undefined;
  const displayName =
    (lang === 'ar' && entry.displayName.ar) ||
    (lang === 'ur' && entry.displayName.ur) ||
    entry.displayName.en;
  return { displayName, type: entry.type };
}

/** Iterate every entry in the L1 static bundle (for reverse name lookup). */
export function forEachStaticRegion(
  fn: (customCode: string, entry: StaticRegionEntry) => void,
): void {
  for (const [customCode, entry] of Object.entries(SAUDI_BUNDLE.regions)) {
    fn(customCode, entry);
  }
}

/** Whether the static bundle is empty (placeholder mode). Useful in dev to gate UI fallbacks. */
export function isStaticRegionBundleEmpty(): boolean {
  return Object.keys(SAUDI_BUNDLE.regions).length === 0;
}

/** When the static bundle was last generated. Drives cache-bust on prod deploys. */
export function getStaticRegionBundleGeneratedAt(): string {
  return SAUDI_BUNDLE._meta.generatedAt;
}
